import dayjs from "dayjs";
import _ from "lodash";
import { useState } from "react";
import { getApi, postApi } from "lib";
import { ADDRESS_LOOKUP, GEOCODE_COORDINATES, MAPS_SUGGESTIONS } from "services";

export const API_ERROR = "Something went wrong!";

export const _differenceBwObj = (obj1, obj2) => {
  return _.pickBy(obj1, (value, key) => !_.isEqual(obj2[key], value));
};

export const fileToFormData = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("fileName", file.name);
  return formData;
};

export const removeCheckboxStyle = {
  mr: 0,
  fontSize: "1.2rem",
  ".MuiFormControlLabel-root": {
    m: 0,
  },
  "&.Mui-checked .MuiSvgIcon-root": {
    bgcolor: "secondary.light",
    borderRadius: 0.5,
    color: "common.white",
  },
};

export const queryPath = ({ page, pageSize = 9, sortBy, state, name }) => {
  var query = "?";
  if (page && pageSize) {
    query = query + `${page ? `page=${page}&pageSize=${pageSize}&` : ""}`;
  }

  if (sortBy) {
    query += `${sortBy ? `sortBy=${sortBy}&` : ""}`;
  }

  if (state) {
    query = query + `${state ? `state=${state}&` : ""}`;
  }
  if (name) {
    query = query + `${name ? `name=${name}` : ""}`;
  }

  return query;
};

export const sortText = (text) => {
  return text?.length > 40 ? `${text?.substring(0, 20)}...` : text;
};

export const isFile = (value) => {
  return value[0] instanceof File;
};

export function maskSensitiveInfo(input) {
  if (input.includes("@")) {
    // Masking email
    let [username, domain] = input.split("@");
    let maskedUsername =
      username.length > 2
        ? username[0] +
          "*".repeat(username.length - 2) +
          username[username.length - 1]
        : username;
    let maskedDomain =
      domain.length > 2
        ? domain[0] + "*".repeat(domain.length - 2) + domain[domain.length - 1]
        : domain;
    return maskedUsername + "@" + maskedDomain;
  } else {
    // Masking phone number
    let maskedNumber =
      input.length > 4
        ? input[0] + "*".repeat(input.length - 4) + input.slice(-3)
        : input;
    return maskedNumber;
  }
}

function UnknownUrlPreview({ src, dummy }) {
  const [stage, setStage] = useState("image");

  if (stage === "image") {
    return <img src={src} alt="" onError={() => setStage("iframe")} />;
  }
  if (stage === "iframe") {
    return (
      <iframe
        style={{ width: "100%", height: "360px", border: "none" }}
        src={src}
        title="File preview"
        onError={() => setStage("failed")}
      ></iframe>
    );
  }
  return (
    <div style={{ textAlign: "center" }}>
      <img src={dummy} alt="" />
      <div>
        <a href={src} target="_blank" rel="noopener noreferrer">
          Preview unavailable — open file
        </a>
      </div>
    </div>
  );
}

export function checkFileType(value, dummy) {
  if (typeof value === "string") {
    if (!value) {
      return <img src={dummy} alt="" />;
    }
    const newValue = value?.split("?")[0];
    // Check if the value is a URL
    if (newValue.match(/\.(jpeg|jpg|png|gif|webp|pdf|svg)$/i)) {
      if (newValue.match(/\.(jpeg|jpg|png|gif|webp|svg)$/i)) {
        return <img src={value} alt="" />;
      } else if (newValue.match(/\.pdf$/i)) {
        return (
          <iframe
            style={{
              width: "100%",
            }}
            src={value}
            frameBorder="0"
          ></iframe>
        );
      }
    } else {
      // No recognizable extension (e.g. a signed/extension-less URL) —
      // attempt to render it rather than silently showing a blank placeholder.
      return <UnknownUrlPreview src={value} dummy={dummy} />;
    }
  } else if (value && value[0] instanceof File) {
    // Check if the value is a File object
    const allowedExtensions = ["jpeg", "jpg", "png", "gif", "webp", "pdf", "svg"];
    const fileExtension = value[0].name.split(".").pop().toLowerCase();
    if (allowedExtensions.includes(fileExtension)) {
      if (fileExtension.match(/(jpeg|jpg|png|gif|webp|svg)$/i)) {
        return <img src={window.URL.createObjectURL(value[0])} alt="" />;
      } else if (fileExtension === "pdf") {
        return (
          <iframe
            style={{
              width: "100%",
            }}
            src={window.URL.createObjectURL(value[0])}
            frameBorder="0"
          ></iframe>
        );
      }
    } else {
      return <img src={dummy} alt="" />;
    }
  } else {
    return <img src={dummy} alt="" />;
  }
}

export const GOOGLE_MAPS_API_KEY =  process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export async function getCoordinates(address) {
  const payload = {
    house_number: address?.house_number?.startsWith("#")
      ? address?.house_number?.replace("#", "")
      : address?.house_number,
    landmark: address?.landmark || "",
    street_1: address?.street_1 || "",
    street_2: address?.street_2 || "",
    city: address?.city || "",
    state: address?.state || "",
    pincode: address?.pincode || "",
    country: address?.country || "",
  };

  const response = await postApi(GEOCODE_COORDINATES, payload);
  const responseData = response?.data;

  const latitude =
    responseData?.latitude ??
    responseData?.data?.latitude ??
    responseData?.results?.[0]?.geometry?.location?.lat;
  const longitude =
    responseData?.longitude ??
    responseData?.data?.longitude ??
    responseData?.results?.[0]?.geometry?.location?.lng;

  return { latitude, longitude };
}

export async function getAddressFromCoordinates(latitude, longitude) {
  const payload = { latitude, longitude };
  const response = await postApi(GEOCODE_COORDINATES, payload);
  const responseData = response?.data;

  const formattedAddress =
    responseData?.formatted_address ??
    responseData?.data?.formatted_address ??
    responseData?.results?.[0]?.formatted_address ??
    "";

  const addressComponents =
    responseData?.address_components ??
    responseData?.data?.address_components ??
    responseData?.results?.[0]?.address_components ??
    [];

  return { formattedAddress, addressComponents };
}

// Looks up a place via the backend's findcare/maps/address/ endpoint, which
// proxies Google's Geocoding API and returns its response unmodified.
// `googleQuery` is the Google geocode query fragment, e.g. "latlng=<lat>,<lng>",
// "place_id=<id>", or "address=<text>" — the backend expects it under its own
// "query" param (query_params.get("query")), so it must be wrapped, not sent raw.
export async function fetchAddressSuggestions(googleQuery) {
  const response = await getApi(`${ADDRESS_LOOKUP}?query=${encodeURIComponent(googleQuery)}`);
  return response?.data?.results || [];
}

export const toLookupResult = (result) => ({
  latitude: result?.geometry?.location?.lat,
  longitude: result?.geometry?.location?.lng,
  formattedAddress: result?.formatted_address || "",
  addressComponents: result?.address_components || [],
  placeId: result?.place_id,
});

// Live-typing suggestions via the backend's findcare/maps/suggestions/
// endpoint, which proxies Google's Places Autocomplete API. A session token
// must be shared across all keystrokes of one search (and the place-details
// lookup that follows selecting a suggestion) for Google's session billing —
// see generateSessionToken() below.
export async function fetchAutocompleteSuggestions(searchPhrase, sessionToken) {
  const response = await getApi(
    `${MAPS_SUGGESTIONS}?search_phrase=${encodeURIComponent(searchPhrase)}&session_token=${encodeURIComponent(sessionToken)}`
  );
  return response?.data?.predictions || [];
}

export const generateSessionToken = () =>
  window.crypto?.randomUUID?.() ||
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });

const getAddressComponent = (addressComponents = [], types = []) =>
  addressComponents.find((component) =>
    types.some((type) => component.types?.includes(type))
  )?.long_name;

export function parseGoogleAddressComponents(
  addressComponents = [],
  formattedAddress = ""
) {
  const firstAddressPart = formattedAddress?.split(",")?.[0]?.trim() || "";
  const plusCode = getAddressComponent(addressComponents, ["plus_code"]);
  const route = getAddressComponent(addressComponents, ["route"]);
  const sublocality1 = getAddressComponent(addressComponents, [
    "sublocality_level_1",
    "neighborhood",
  ]);
  const sublocality2 = getAddressComponent(addressComponents, [
    "sublocality_level_2",
    "sublocality_level_3",
  ]);
  const premise = getAddressComponent(addressComponents, [
    "premise",
    "point_of_interest",
    "establishment",
  ]);

  return {
    city:
      getAddressComponent(addressComponents, ["locality"]) ||
      getAddressComponent(addressComponents, ["administrative_area_level_3"]) ||
      sublocality1 ||
      "",
    state:
      getAddressComponent(addressComponents, ["administrative_area_level_1"]) ||
      "",
    pincode: getAddressComponent(addressComponents, ["postal_code"]) || "",
    country: getAddressComponent(addressComponents, ["country"]) || "",
    house_number: premise || "",
    street_1: route || sublocality1 || firstAddressPart || "",
    street_2: sublocality2 || route || sublocality1 || "",
    landmark: sublocality1 || plusCode || "",
  };
}



export function isTwoHoursBeforeDelivery(deliveryTime) {
  // Get the current time
  const currentTime = dayjs();

  // Get the delivery time as a Day.js object
  const deliveryTimeObj = dayjs(deliveryTime);
  // Calculate the duration between the current time and the delivery time
  const timeDifference = deliveryTimeObj.diff(currentTime, "hour");

  // Check if the time difference is less than or equal to 2 hours
  return timeDifference;
}
